import React, { useCallback, useMemo, useState } from 'react';
import styles from './ExploreSongs.module.css';
import PaginationTable from '../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { CellProps, Column } from 'react-table';
import useUrlState from '../../../../Hooks/useUrlState';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import Image from '../../../../ui/Image/Image';
import { ReactComponent as MusicIcon } from '../../../../assets/MusicIcon.svg';
import { formatNumberToKNotation, numberWithCommas, roundTo2Dp, showNotification } from '../../../../helpers';
import BarsSmallChart from '../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import { GenericDropdownOption, OrderingParams, OrderingValues } from '../../../../App.types';
import { StylesConfig, ValueType } from 'react-select';
import Select from '../../../../ui/DataEntry/Select/Select';
import cn from 'classnames';
import Button from '../../../../ui/Buttons/Button/Button';
import CreateSongModal from '../CreateSongModal/CreateSongModal';
import { getSongTiktokStats, usePaginatedFetch, SongTiktokStats } from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import SongDetailsModal from '../SongDetailsModal/SongDetailsModal';
import { ProtectedByUserGroups } from '../../../Auth/components/ProtectedByUserGroup/ProtectedByUserGroups';
import AddSongToSongCollection from '../../../SongCollection/features/AddSongToSongCollection/AddSongToSongCollection';
import { SongCollectionsListContextProvider } from '../../../SongCollection/contexts/SongCollectionsListContext';

type ExploreSongsSortableKeys = keyof Pick<
    SongTiktokStats,
    | 'total_video_count'
    | 'total_video_count_daily_change'
    | 'total_video_count_daily_change_relative'
    | 'total_video_count_weekly_change'
    | 'total_video_count_weekly_change_relative'
>;

type UrlState = {
    songId: number | undefined;
    page: number;
    pageSize: number;
    search: string;
} & OrderingParams<ExploreSongsSortableKeys>;

type OrderingOption = GenericDropdownOption<OrderingValues<ExploreSongsSortableKeys>>;
const orderingOptions: OrderingOption[] = [
    { label: 'Total Videos', value: '-total_video_count' },
    { label: 'Daily Growth (# videos)', value: '-total_video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-total_video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-total_video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-total_video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const ExploreSongs = () => {
    const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);

    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        pageSize: 10,
        search: '',
        ordering: '-total_video_count_daily_change',
        songId: undefined,
    });

    const page = Number(urlState.page);
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = Number(urlState.pageSize);
    const setPageSize = (pageSize: number | undefined) => setUrlState({ pageSize });
    const search = urlState.search ?? '';
    const setSearch = (search: string) => setUrlState({ search, page: 1 });
    const ordering = orderingOptions.find((option) => urlState.ordering === option.value);
    const setOrdering = (ordering: ValueType<OrderingOption, false>) => setUrlState({ ordering: ordering?.value });

    const {
        page: songStatsPage,
        loading,
        count,
        getCurrentPage,
    } = usePaginatedFetch(getSongTiktokStats, {
        page,
        page_size: pageSize,
        search,
        ordering: ordering?.value,
    });

    const selectedRow = songStatsPage.find((stats) => urlState.songId && stats.song.id === Number(urlState.songId));

    const isSongDetailsModalOpen = Boolean(urlState.songId);

    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (ordering?.value.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [ordering?.value]
    );

    const onSongAdded = useCallback(() => {
        setIsAddSongModalOpen(false);
        showNotification('Successfully added song', 'info');
        getCurrentPage();
    }, [getCurrentPage]);

    const columns = useMemo<Column<SongTiktokStats>[]>(
        () => [
            {
                Header: 'Song',
                accessor: 'song',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.songContainer}>
                            <Image className={styles.songImage} src={value.image ?? ''} alt={value.title} />
                            <div className={styles.songDetails}>
                                <a
                                    href={value.spotify_song_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.songTitle}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {value.title}
                                </a>
                                <p className={styles.songAuthor}>
                                    <MusicIcon className={styles.musicIcon} />
                                    {value.artists.map((a) => a.name).join(', ')}
                                </p>
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: 'Audios',
                accessor: 'audio_count',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{numberWithCommas(value)}</>;
                },
            },
            {
                Header: 'Creations',
                accessor: 'total_video_count',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    const badgeValue =
                        ordering && ordering?.value !== '-total_video_count'
                            ? Number(original[ordering.value.slice(1) as ExploreSongsSortableKeys])
                            : null;

                    return (
                        <>
                            <span>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'standardised_data',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.growth}>
                            <BarsSmallChart
                                color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                                values={value.daily_total_video_counts
                                    .slice(-7)
                                    .map((item) => ({ ...item, value: item.change }))}
                            />
                        </div>
                    );
                },
            },
            {
                Header: 'Add to collection',
                accessor: 'song_collection_ids',
                Cell: ({ value, row: { original } }: CellProps<SongTiktokStats>) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <AddSongToSongCollection songId={original.song.id} collectionIds={value} />;
                },
            },
        ],
        [formatVideoCountBadgeValue, loading, ordering]
    );

    useAbortableEffect(
        (signal) => {
            getCurrentPage({ signal });
        },
        [getCurrentPage]
    );

    return (
        <>
            <div className={styles.container}>
                <h1 className={styles.title}>Songs</h1>

                <div className={styles.toolbar}>
                    <SearchInput className={styles.search} value={search} onChange={setSearch} />

                    <div className={styles.toolbarActions}>
                        <div className={styles.toolbarItem}>
                            <label className={styles.toolbarItemLabel}>Sort by</label>
                            <Select
                                className={styles.orderingSelect}
                                menuPortalTarget={document.body}
                                styles={sortingSelectStyles}
                                options={orderingOptions}
                                value={ordering}
                                onChange={setOrdering}
                            />
                        </div>

                        <ProtectedByUserGroups groups={['user_admin']}>
                            <Button type="filled" color="black" onClick={() => setIsAddSongModalOpen(true)}>
                                Add new
                            </Button>
                        </ProtectedByUserGroups>
                    </div>
                </div>

                <SongCollectionsListContextProvider>
                    <PaginationTable
                        tableClassName={styles.table}
                        loading={loading}
                        data={songStatsPage}
                        columns={columns}
                        count={count}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        onRowClick={(row) => {
                            setUrlState({ songId: row.song.id });
                        }}
                    />
                </SongCollectionsListContextProvider>
            </div>

            <CreateSongModal
                isModalOpen={isAddSongModalOpen}
                closeModal={() => setIsAddSongModalOpen(false)}
                onCreated={onSongAdded}
            />

            <SongDetailsModal
                isOpen={isSongDetailsModalOpen}
                onClose={() => setUrlState({ songId: undefined })}
                songId={urlState.songId ? Number(urlState.songId) : null}
                songTiktokStats={selectedRow}
            />
        </>
    );
};

export default ExploreSongs;
